/**
 * My orders: what this account has asked for, and where each stands.
 *
 * "Confirmed" comes with the nursery's order number, which is what a customer
 * quotes on the phone. A declined order says why, in the nursery's words. One
 * still waiting can be cancelled; once the nursery has decided it, the answer
 * is where it stands rather than an error - the order moved on first.
 *
 * A customer may ask for any quantity, and the nursery decides what it can
 * supply when it confirms (V5). So each line shows both: what was asked, and
 * once confirmed, what the nursery will send - "Not supplied" where that is
 * none, rather than a zero that reads like a typing slip. The words are
 * `standing.ts`'s.
 *
 * Arriving from a Place, the top says what happened: the order is with the
 * nursery, or an earlier try at it already was and the changes made since
 * were not sent.
 */

import { cents, formatZAR } from '@smf/core';
import { useCallback, useEffect, useState } from 'react';

import { readable } from './basket.js';
import type { Settled } from './placing.js';
import type { PortalApi, WebOrder } from './portal-api.js';
import { confirmedOf, standing, toneOf } from './standing.js';

function Arrived({ settled }: { readonly settled: Settled }): React.JSX.Element {
  if (settled.kind === 'placed') {
    return (
      <p className="notice notice--good" role="status">
        Order {settled.reference} is with the nursery. It shows here as confirmed, with
        an order number and how many of each they can supply, once they have checked it.
      </p>
    );
  }
  // Said as an alert: the customer changed the basket and believes the change
  // went, and it did not.
  return (
    <p className="notice notice--warn" role="alert">
      Your earlier order
      {settled.reference === undefined ? '' : ` ${settled.reference}`} did reach the
      nursery. Your changes were not sent - place them as a new order if you still want
      them.
    </p>
  );
}

export function OrdersView({
  api,
  placed,
}: {
  readonly api: PortalApi;
  /** How the last Place ended, to say so at the top. */
  readonly placed?: Settled | undefined;
}): React.JSX.Element {
  const [orders, setOrders] = useState<readonly WebOrder[] | undefined>(undefined);
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const [cancelling, setCancelling] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const answer = await api.orders();
    if (answer.ok) setOrders(answer.value);
    else setProblem('We could not read your orders. Try again in a moment.');
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  async function cancel(order: WebOrder): Promise<void> {
    setBusy(true);
    setProblem(undefined);
    const answer = await api.cancel(order.id);
    setBusy(false);
    setCancelling(undefined);
    if (!answer.ok) {
      setProblem('That order could not be cancelled. Try again, or call the nursery.');
    } else if (answer.value.status === 'decided') {
      setProblem(
        `The nursery had already dealt with ${order.reference}, so it was not cancelled.`,
      );
    }
    await load();
  }

  return (
    <section className="stack" aria-labelledby="orders-title">
      <h2 id="orders-title">My orders</h2>
      {placed === undefined ? null : <Arrived settled={placed} />}
      {problem === undefined ? null : (
        <p className="notice notice--bad" role="alert">
          {problem}
        </p>
      )}
      {orders === undefined ? (
        <p className="faint">Reading your orders…</p>
      ) : orders.length === 0 ? (
        <p className="faint">No orders yet.</p>
      ) : (
        <ul className="orders" aria-label="Your orders">
          {orders.map((order) => (
            <li key={order.id} className="order">
              <div className="order__head">
                <strong>{order.reference}</strong>
                <span className={toneOf(order)}>{standing(order)}</span>
              </div>
              <span className="fine faint">
                Placed {order.placedAt.slice(0, 10)}
                {order.requiredDate === undefined
                  ? ''
                  : ` · wanted by ${order.requiredDate}`}
              </span>
              {order.decisionReason === undefined ? null : (
                <p className="fine">{order.decisionReason}</p>
              )}
              <ul className="lines" aria-label={`Plants in ${order.reference}`}>
                {order.lines.map((line) => {
                  const confirmed = confirmedOf(order, line);
                  return (
                    <li key={line.lineNo} className="line line--order">
                      <span className="line__name">{line.description}</span>
                      <span className="line__figure">
                        {line.shownUnitPriceCents === undefined
                          ? 'On confirmation'
                          : `${formatZAR(cents(line.shownUnitPriceCents))} each`}
                      </span>
                      <span className="line__quantities">
                        <span>Asked {readable(line.quantity)}</span>
                        {confirmed === undefined ? null : (
                          <span
                            className={
                              confirmed.short
                                ? 'line__confirmed line__confirmed--short'
                                : 'line__confirmed'
                            }
                          >
                            {confirmed.words}
                          </span>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>
              {order.status !== 'received' ? null : cancelling === order.id ? (
                <div className="row">
                  <button
                    className="button button--danger"
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      void cancel(order);
                    }}
                  >
                    Cancel {order.reference}
                  </button>
                  <button
                    className="button"
                    type="button"
                    onClick={() => {
                      setCancelling(undefined);
                    }}
                  >
                    Keep it
                  </button>
                </div>
              ) : (
                <button
                  className="button button--quiet"
                  type="button"
                  onClick={() => {
                    setCancelling(order.id);
                  }}
                >
                  Cancel this order
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
